import { useState } from 'react';
import clsx from 'clsx';
import { convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Editor, EditorProps } from 'react-draft-wysiwyg';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FieldLabel } from '@rossum/ui';
import type { FieldLabelProps } from '@rossum/ui';
import { alpha, FormHelperText, Stack } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useWysiwygHtmlDecorator } from '../../../../containers/Queue/components/TemplateMessageEditor/hooks';
import { ControlledFieldProps, LabeledFieldProps } from '../../utils';
import styles from './styles.module.sass';
import editorToolbarConfig from './wysiwygControlToolbarConfig';

type WysiwygControlProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ControlledFieldProps<TFieldValues, TName> &
  LabeledFieldProps & {
    // this has to be passed explicitly now
    initialValue: string;
    disabled?: boolean;
    error?: string;
    onFocus?: () => void;
    onBlur?: () => void;
    required?: boolean;
    customButtons?: EditorProps['toolbarCustomButtons'];
  };

const WysiwygControl = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  FieldLabelProps,
  initialValue,
  disabled,
  error,
  required,
  onFocus,
  onBlur,
  customButtons,
}: WysiwygControlProps<TFieldValues, TName>) => {
  const { control, name } = ControllerProps;

  const resolvedFieldLabelProps: FieldLabelProps = {
    htmlFor: name,
    required,
    ...FieldLabelProps,
    label: FieldLabelProps?.label ?? name,
  };

  const [, editorState, setEditorState] = useWysiwygHtmlDecorator({
    msgString: initialValue,
  });

  // oxlint-disable-next-line react-ext/hook-use-state -- write-only state, value intentionally unused
  const [_focused, setFocused] = useState(false);

  const wysiwygFocused = () => {
    if (!disabled) {
      setFocused(true);
      onFocus?.();
    }
  };

  const wysiwygBlurred = () => {
    if (!disabled) {
      setFocused(false);
      onBlur?.();
    }
  };

  return (
    <FieldLabel {...resolvedFieldLabelProps}>
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => (
          <Stack
            spacing={1}
            sx={{
              [`& .${styles['EditorWrapper']}`]: {
                borderRadius: borderRadius('s'),
                boxShadow: t =>
                  `0 0 0 1px ${alpha(t.palette.text.primary, 0.23)}`,
                '&:hover': {
                  boxShadow: t =>
                    `0 0 0 1px ${alpha(t.palette.text.primary, 1)}`,
                },
                '&:focus-within': {
                  boxShadow: t => `0 0 0 2px ${t.palette.primary.main}`,
                },
              },
            }}
          >
            <Editor
              editorRef={field.ref}
              editorState={editorState}
              onFocus={() => wysiwygFocused()}
              onBlur={() => wysiwygBlurred()}
              onEditorStateChange={newEditorState => {
                field.onChange(
                  draftToHtml(convertToRaw(newEditorState.getCurrentContent()))
                );
                setEditorState(newEditorState);
              }}
              stripPastedStyles
              toolbarClassName={clsx(
                styles['EditorToolbar'],
                disabled && styles['EditorToolbarDisabled']
              )}
              editorClassName={clsx(
                styles['EditorContent'],
                error && styles['EditorContentError'],
                (formState.isSubmitting || disabled) &&
                  styles['EditorContentDisabled']
              )}
              wrapperClassName={styles['EditorWrapper']}
              toolbar={editorToolbarConfig}
              readOnly={formState.isSubmitting || disabled}
              toolbarCustomButtons={customButtons ?? []}
            />
            {fieldState?.error && (
              <FormHelperText error>{fieldState.error?.message}</FormHelperText>
            )}
          </Stack>
        )}
      />
    </FieldLabel>
  );
};

export const WysiwygControl2 = ({
  value,
  onChange,
  disabled,
  customButtons,
}: {
  value: EditorState;
  onChange: (editorState: EditorState) => void;
  disabled?: boolean;
  customButtons?: EditorProps['toolbarCustomButtons'];
}) => {
  return (
    <Stack
      sx={{
        [`& .${styles['EditorWrapper']}`]: {
          borderRadius: borderRadius('s'),
          boxShadow: t => `0 0 0 1px ${alpha(t.palette.text.primary, 0.23)}`,
          '&:hover': {
            boxShadow: t => `0 0 0 1px ${alpha(t.palette.text.primary, 1)}`,
          },
          '&:focus-within': {
            boxShadow: t => `0 0 0 2px ${t.palette.primary.main}`,
          },
        },
      }}
    >
      <Editor
        editorState={value}
        onEditorStateChange={editorState => {
          if (!disabled) {
            onChange(editorState);
          }
        }}
        stripPastedStyles
        toolbarClassName={clsx(
          styles['EditorToolbar'],
          disabled && styles['EditorToolbarDisabled']
        )}
        editorClassName={clsx(
          styles['EditorContent'],
          disabled && styles['EditorContentDisabled']
        )}
        wrapperClassName={styles['EditorWrapper']}
        toolbar={editorToolbarConfig}
        readOnly={disabled}
        toolbarCustomButtons={customButtons ?? []}
      />
    </Stack>
  );
};

export default WysiwygControl;
